import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Icon from './Icon';
import { useModal } from '../context/ModalContext';
import '../styles/NotificationBell.css';

// Catches a new notification created elsewhere (another user's action, a
// background job) without requiring a manual page refresh.
const POLL_INTERVAL_MS = 20000;

const NotificationBell = ({ user }) => {
  const modal = useModal();
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get('/api/notifications', { withCredentials: true });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(prev => {
      if (!prev) fetchNotifications();
      return !prev;
    });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleDismissClick = async (e, notification) => {
    e.stopPropagation();
    try {
      await axios.post(`/api/notifications/${notification._id}/dismiss`, {}, { withCredentials: true });
      setNotifications(prev => prev.filter(n => n._id !== notification._id));
    } catch (error) {
      console.error('Error dismissing notification:', error);
      modal.error('Failed to dismiss notification');
    }
  };

  const getEntityDisplay = (notification) => {
    if (notification.entity_logo) {
      return <img src={notification.entity_logo} alt={notification.entity_name || 'Entity'} className="notification-entity-logo" />;
    }
    if (notification.entity_name) {
      return (
        <div className="notification-entity-placeholder">
          <span>{notification.entity_name.charAt(0).toUpperCase()}</span>
        </div>
      );
    }
    return (
      <div className="notification-entity-placeholder">
        <Icon name="notification-bell" size="1rem" />
      </div>
    );
  };

  return (
    <div className="notification-bell-wrapper" ref={wrapperRef}>
      <button
        className="notification-bell-btn"
        onClick={handleToggle}
        title="Notifications"
        aria-label="Notifications"
      >
        <Icon name="notification-bell" size="1.5rem" />
        {notifications.length > 0 && <span className="notification-dot" />}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <span>Notifications</span>
          </div>
          <div className="notification-dropdown-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">You're all caught up</div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification._id}
                  className="notification-row"
                >
                  {getEntityDisplay(notification)}
                  <div className="notification-row-content">
                    <strong>{notification.title}</strong>
                    <div className="notification-row-message">{notification.message}</div>
                    <div className="notification-row-time">{formatRelativeTime(notification.created_at)}</div>
                  </div>
                  <button
                    className="notification-dismiss-btn"
                    onClick={(e) => handleDismissClick(e, notification)}
                    title="Dismiss"
                    aria-label="Dismiss"
                  >
                    <Icon name="close-x" size="0.75rem" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
